"use client";

import { v4 as uuidv4 } from "uuid";
import { useStore } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistStorage } from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { getAdapter } from "@/app/[tenant]/shop/adapters";
import { unitPriceFor, withLineQuantity } from "@/app/[tenant]/shop/cart-line-utils";
import { trackShopAddToCart, trackShopRemoveFromCart } from "@/lib/gtm-shop-events";
import { useToast } from "@/lib/use-toast";
import { ItemType } from "@/types/enums/item-type";
import type { ShopCartLine, ShopCartState } from "@/types/shop";
const STORAGE_KEY_PREFIX = "decoda:purchase-cart:";
const STORE_VERSION = 1;
const storageKey = (tenantSlug: string) => `${STORAGE_KEY_PREFIX}${tenantSlug}`;
function withCheckoutNonce(state: ShopCartState, newCheckoutNonce?: string): ShopCartState {
  if (state.lines.length === 0) {
    return {
      ...state,
      checkoutNonce: undefined
    };
  }
  if (state.checkoutNonce) return state;
  return {
    ...state,
    checkoutNonce: newCheckoutNonce ?? uuidv4()
  };
}
function migrateLegacyImage<T extends {
  imageUrl?: string | null;
}>(value: T & {
  image?: unknown;
}): T {
  const {
    image,
    ...withoutLegacyImage
  } = value;
  if (withoutLegacyImage.imageUrl === undefined && (typeof image === "string" || image === null)) {
    return {
      ...withoutLegacyImage,
      imageUrl: image
    } as T;
  }
  return withoutLegacyImage as T;
}
function normalize(line: ShopCartLine): ShopCartLine {
  const adapter = getAdapter(line.itemType);
  const normalized = adapter?.normalizeLine?.(line) ?? line;
  const legacySnapshot = normalized.snapshot as typeof normalized.snapshot & {
    image?: unknown;
    promotionName?: unknown;
  };
  const {
    promotionName: _promotionName,
    ...snapshot
  } = legacySnapshot;
  const safeSnapshot = migrateLegacyImage(snapshot);
  if (Array.isArray(safeSnapshot.packageIncludedItems)) {
    safeSnapshot.packageIncludedItems = safeSnapshot.packageIncludedItems.map(item => migrateLegacyImage(item as typeof item & {
      image?: unknown;
    }));
  }
  if (normalized.itemType === ItemType.PACKAGE || normalized.itemType === ItemType.MEMBERSHIP || normalized.itemType === ItemType.SERVICE || normalized.itemType === ItemType.PRODUCT || normalized.itemType === ItemType.MEDICATION) {
    return {
      ...normalized,
      unitPriceCents: normalized.expectedUnitPriceCents ?? normalized.unitPriceCents,
      snapshot: safeSnapshot
    };
  }
  return {
    ...normalized,
    snapshot: safeSnapshot
  };
}
type PersistedShopCartState = ShopCartState;
const lineItemKey = (line: ShopCartLine) => "itemId" in line ? `${line.itemType}:${line.itemId}` : line.lineId;

/** Reports an edited line as the units added or removed. */
function trackLineChange(previous: ShopCartLine, next: ShopCartLine) {
  if (lineItemKey(previous) !== lineItemKey(next)) {
    trackShopRemoveFromCart(previous);
    trackShopAddToCart(next);
    return;
  }
  const delta = next.quantity - previous.quantity;
  if (delta > 0) trackShopAddToCart(next, delta);
  if (delta < 0) trackShopRemoveFromCart(previous, -delta);
}
type ShopCartStore = ShopCartState & {
  addLine: (line: ShopCartLine) => boolean;
  replaceLine: (lineId: string, line: ShopCartLine) => void;
  removeLine: (lineId: string) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  clear: () => void;
};

/**
 * Keep the established per-tenant localStorage shape while letting Zustand own
 * hydration and writes. Reading also migrates legacy cart lines before they
 * enter the store, so personalized promotion data is not restored.
 */
function createShopCartStorage(tenantSlug: string): PersistStorage<PersistedShopCartState> {
  return {
    getItem: () => {
      if (typeof window === "undefined") return null;
      const raw = window.localStorage.getItem(storageKey(tenantSlug));
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as Partial<ShopCartState>;
        if (parsed.tenantSlug !== tenantSlug || !Array.isArray(parsed.lines)) {
          return null;
        }
        const state = withCheckoutNonce({
          tenantSlug,
          lines: parsed.lines.map(normalize),
          checkoutNonce: typeof parsed.checkoutNonce === "string" && parsed.checkoutNonce ? parsed.checkoutNonce : undefined
        });
        if (state.lines.length === 0) {
          window.localStorage.removeItem(storageKey(tenantSlug));
        } else {
          window.localStorage.setItem(storageKey(tenantSlug), JSON.stringify(state));
        }
        return {
          state,
          version: STORE_VERSION
        };
      } catch {
        window.localStorage.removeItem(storageKey(tenantSlug));
        return null;
      }
    },
    setItem: (_name, value) => {
      if (typeof window === "undefined") return;
      if (value.state.lines.length === 0) {
        window.localStorage.removeItem(storageKey(tenantSlug));
        return;
      }
      window.localStorage.setItem(storageKey(tenantSlug), JSON.stringify(value.state));
    },
    removeItem: () => {
      if (typeof window === "undefined") return;
      window.localStorage.removeItem(storageKey(tenantSlug));
    }
  };
}
function createShopCartStore(tenantSlug: string) {
  return createStore<ShopCartStore>()(persist((set, get) => {
    const update = (mutate: (previous: ShopCartState) => ShopCartState) => {
      const previous = get();
      const mutated = mutate(previous);
      if (mutated === previous) return false;
      set(withCheckoutNonce(mutated, uuidv4()));
      return true;
    };
    return {
      tenantSlug,
      lines: [],
      addLine: line => {
        const addingMembership = line.itemType === ItemType.MEMBERSHIP;
        return update(previous => {
          const hasMembership = previous.lines.some(existing => existing.itemType === ItemType.MEMBERSHIP);
          const hasOrdinaryItems = previous.lines.some(existing => existing.itemType !== ItemType.MEMBERSHIP);
          if (addingMembership && hasOrdinaryItems || !addingMembership && hasMembership) {
            return previous;
          }
          return {
            ...previous,
            // A patient selects one plan per checkout. Choosing another
            // membership replaces the previous membership selection.
            lines: [...(addingMembership ? previous.lines.filter(existing => existing.itemType !== ItemType.MEMBERSHIP) : previous.lines), normalize(line)]
          };
        });
      },
      replaceLine: (lineId, line) => {
        update(previous => ({
          ...previous,
          lines: previous.lines.map(existing => existing.lineId === lineId ? normalize(line) : existing)
        }));
      },
      removeLine: lineId => {
        update(previous => ({
          ...previous,
          lines: previous.lines.filter(line => line.lineId !== lineId)
        }));
      },
      updateQuantity: (lineId, quantity) => {
        update(previous => ({
          ...previous,
          lines: previous.lines.map(line => line.lineId === lineId ? normalize(withLineQuantity(line, quantity)) : line)
        }));
      },
      clear: () => set({
        tenantSlug,
        lines: [],
        checkoutNonce: undefined
      })
    };
  }, {
    name: storageKey(tenantSlug),
    storage: createShopCartStorage(tenantSlug),
    partialize: state => ({
      tenantSlug: state.tenantSlug,
      lines: state.lines,
      checkoutNonce: state.checkoutNonce
    }),
    version: STORE_VERSION,
    // Match the server's empty initial cart, then restore localStorage after
    // mount to avoid a hydration mismatch in this client component.
    skipHydration: true
  }));
}

/**
 * Tenant-scoped online purchase cart. Zustand owns client state and persistence;
 * a scoped vanilla store avoids sharing one tenant's SSR state with another.
 */
export function useShopCart(tenantSlug: string) {
  const {
    toast
  } = useToast();
  const store = useMemo(() => createShopCartStore(tenantSlug), [tenantSlug]);
  const state = useStore(store);

  // Before paint, so a click can't add a line that the restored cart then overwrites.
  useLayoutEffect(() => {
    void store.persist.rehydrate();
  }, [store]);
  const addLine = useCallback((line: ShopCartLine) => {
    // Choosing a plan replaces the membership already in the cart.
    const displacedMembership = line.itemType === ItemType.MEMBERSHIP ? state.lines.find(existing => existing.itemType === ItemType.MEMBERSHIP) : undefined;
    if (state.addLine(line)) {
      if (displacedMembership) trackShopRemoveFromCart(displacedMembership);
      trackShopAddToCart(line);
      return true;
    }
    toast({
      variant: "warning",
      title: "Purchase membership separately",
      description: "Complete or clear your current cart before adding this item."
    });
    return false;
  }, [state.lines, state.addLine, toast]);
  const removeLine = useCallback((lineId: string) => {
    const line_0 = state.lines.find(existing_0 => existing_0.lineId === lineId);
    state.removeLine(lineId);
    if (line_0) trackShopRemoveFromCart(line_0);
  }, [state.lines, state.removeLine]);
  const updateQuantity = useCallback((lineId_0: string, quantity: number) => {
    const line_1 = state.lines.find(existing_1 => existing_1.lineId === lineId_0);
    state.updateQuantity(lineId_0, quantity);
    if (line_1) trackLineChange(line_1, withLineQuantity(line_1, quantity));
  }, [state.lines, state.updateQuantity]);
  const replaceLine = useCallback((lineId_1: string, line_2: ShopCartLine) => {
    const previous = state.lines.find(existing_2 => existing_2.lineId === lineId_1);
    state.replaceLine(lineId_1, line_2);
    if (previous) {
      trackLineChange(previous, withLineQuantity(line_2, line_2.quantity));
    }
  }, [state.lines, state.replaceLine]);
  const totalCents = useMemo(() => state.lines.reduce((sum, line_3) => sum + unitPriceFor(line_3) * line_3.quantity, 0), [state.lines]);
  return {
    lines: state.lines,
    checkoutNonce: state.checkoutNonce,
    totalCents,
    isEmpty: state.lines.length === 0,
    addLine,
    replaceLine,
    removeLine,
    updateQuantity,
    clear: state.clear
  };
}
export type ShopCart = ReturnType<typeof useShopCart>;